import { useState } from "react";
import TextInput from "../forms/TextInput";
import SelectInput from "../forms/SelectInput";
import { downloadIcs, emailEvent } from "../../js/utils/calendarInvite";

const TYPES = [
  { value: "appointment", label: "Appointment" },
  { value: "showing", label: "Showing" },
  { value: "escrow", label: "Escrow" },
  { value: "task", label: "Task" },
  { value: "reminder", label: "Reminder" },
];

const UNITS = [
  { value: "min", label: "minute(s)" },
  { value: "hour", label: "hour(s)" },
  { value: "day", label: "day(s)" },
  { value: "month", label: "month(s)" },
];

const REPEAT = [
  { value: "none", label: "Does not repeat" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

export const EMPTY_EVENT = {
  title: "",
  date: new Date().toISOString().slice(0, 10),
  time: "10:00",
  type: "appointment",
  location: "",
  description: "",
  contactName: "",
  notes: "",
  repeat: "none",
  sendEmail: false,
  sendPhoneCalendar: false,
  sendDashboardAlert: true,
  reminders: [{ amount: 1, unit: "hour" }],
};

export default function EventDetailsForm({ value, onChange, onSubmit, submitLabel = "Save event" }) {
  const form = value || EMPTY_EVENT;
  const [inviteNote, setInviteNote] = useState("");

  function update(event) {
    const { name, value: next, type, checked } = event.target;
    onChange({ ...form, [name]: type === "checkbox" ? checked : next });
  }

  function setReminder(index, patch) {
    const reminders = form.reminders.map((item, i) => (i === index ? { ...item, ...patch } : item));
    onChange({ ...form, reminders });
  }

  function addReminder() {
    onChange({ ...form, reminders: [...form.reminders, { amount: 1, unit: "day" }] });
  }

  function removeReminder(index) {
    onChange({ ...form, reminders: form.reminders.filter((_, i) => i !== index) });
  }

  function handleSubmit(event) {
    event.preventDefault();
    onSubmit?.(form);
    if (form.sendEmail) emailEvent(form);
    if (form.sendPhoneCalendar) {
      downloadIcs(form);
      setInviteNote("Calendar file downloaded — open it on iPhone (Apple Calendar) or Android to save the event.");
    } else {
      setInviteNote("");
    }
  }

  return (
    <form className="card event-details" data-tour="calendar-options" onSubmit={handleSubmit}>
      <header className="event-details__head">
        <h2>Event details</h2>
        <p className="muted">Date lives here. The grid only shows dots.</p>
      </header>
      <div className="event-details__scroll" data-tour="calendar-options-scroll">
        <section className="event-details__block">
          <TextInput label="Title" name="title" value={form.title} onChange={update} required />
          <div className="event-details__row">
            <label className="field">
              <span className="field-label">Date</span>
              <input className="field-input" type="date" name="date" value={form.date} onChange={update} required />
            </label>
            <TextInput label="Time" name="time" type="time" value={form.time} onChange={update} required />
          </div>
          <SelectInput label="Type" name="type" value={form.type} onChange={update} options={TYPES} />
        </section>
        <section className="event-details__block">
          <fieldset className="field event-details__reminders">
            <legend className="field-label">Reminders</legend>
            {form.reminders.map((reminder, index) => (
              <div key={`${index}-${reminder.unit}`} className="event-details__reminder">
                <input
                  className="field-input"
                  type="number"
                  min="1"
                  max="99"
                  value={reminder.amount}
                  onChange={(event) => setReminder(index, { amount: Number(event.target.value) })}
                  aria-label="Reminder amount"
                />
                <select
                  className="field-select"
                  value={reminder.unit}
                  onChange={(event) => setReminder(index, { unit: event.target.value })}
                  aria-label="Reminder unit"
                >
                  {UNITS.map((unit) => (
                    <option key={unit.value} value={unit.value}>
                      {unit.label}
                    </option>
                  ))}
                </select>
                {form.reminders.length > 1 ? (
                  <button type="button" className="text-btn" onClick={() => removeReminder(index)}>
                    Remove
                  </button>
                ) : null}
              </div>
            ))}
            <button type="button" className="text-btn" onClick={addReminder}>
              Add reminder
            </button>
          </fieldset>
          <SelectInput label="Repeat" name="repeat" value={form.repeat} onChange={update} options={REPEAT} />
        </section>
        <section className="event-details__block">
          <TextInput label="Location" name="location" value={form.location} onChange={update} />
          <label className="field">
            <span className="field-label">Description</span>
            <textarea className="field-textarea" name="description" value={form.description} onChange={update} rows={2} />
          </label>
          <TextInput label="Contact name" name="contactName" value={form.contactName} onChange={update} />
          <label className="field">
            <span className="field-label">Notes</span>
            <textarea className="field-textarea" name="notes" value={form.notes} onChange={update} rows={2} />
          </label>
        </section>
        <section className="event-details__block">
          <label className="checkbox-item">
            <input type="checkbox" name="sendEmail" checked={form.sendEmail} onChange={update} />
            <span>Send to email</span>
          </label>
          <label className="checkbox-item">
            <input type="checkbox" name="sendPhoneCalendar" checked={form.sendPhoneCalendar} onChange={update} />
            <span>Phone calendar (.ics)</span>
          </label>
          <label className="checkbox-item">
            <input type="checkbox" name="sendDashboardAlert" checked={form.sendDashboardAlert} onChange={update} />
            <span>Dashboard alert</span>
          </label>
        </section>
      </div>
      <footer className="event-details__foot">
        {inviteNote ? <p className="confirm-banner">{inviteNote}</p> : null}
        <button type="submit" className="primary-btn">
          {submitLabel}
        </button>
      </footer>
    </form>
  );
}
